import { LabelSelector } from "@/components/PredictionMarket/LabelSelector";
import { MarketCardListItem } from "@/components/PredictionMarket/MarketCardList/MarketCardListItem";
import { MultipleChoices } from "@/components/PredictionMarket/MarketCardList/MultipleChoices";
import { SearchInput } from "@/components/PredictionMarket/SearchInput";
import { SelectSortPredictionMarket } from "@/components/PredictionMarket/SortPredictionMarket";
import { SubLabelSelector } from "@/components/PredictionMarket/SubLabelSelector";
import { Skeleton } from "@/components/ui/skeleton";
import { useHomePageStore } from "@/store";
import { isDemoMode } from "@/utils/demoUser";
import { DEFAULT_LABELS_PREDICTION_MARKET } from "@roman/shared/constants/predictionMarket";
import { useGetActiveLabels } from "@roman/shared/hooks/PredictionMarket/useGetActiveLabels";
import { useGetPublicPredictionMarkets } from "@roman/shared/hooks/PredictionMarket/useGetPublicPredictionMarkets";
import {
  type LabelData,
  type SubLabelData,
  getPredictionMarketLabelQueries,
  getQueryFilterSpecification,
} from "@roman/shared/hooks/PredictionMarket/usePredictionMarketLabels";
import { useInfiniteScroll } from "@roman/shared/hooks/useInfiniteScroll";
import { generateDemoMarkets } from "@roman/shared/utils/predictionMarket/generateDemoMarkets";
import {
  PredictionMarketType,
  getPredictionMarketType,
  normalizeChoicesPercentage,
} from "@roman/shared/utils/predictionMarket/utils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export const PredictionMarketTop = () => {
  const isDemo = isDemoMode();
  const navigate = useNavigate();
  const selectedLabel = useHomePageStore((state) => state.selectedLabel);
  const setSelectedLabel = useHomePageStore((state) => state.setSelectedLabel);

  const selectedSubLabel = useHomePageStore((state) => state.selectedSubLabel);
  const setSelectedSubLabel = useHomePageStore((state) => state.setSelectedSubLabel);

  const selectedSortPredictionMarketType = useHomePageStore((state) => state.selectedSortPredictionMarketType);
  const setSelectedSortPredictionMarketType = useHomePageStore((state) => state.setSelectedSortPredictionMarketType);

  const [headerHeight, setHeaderHeight] = useState(0);

  const { queryLabel, orderBy, querySubLabel } = getPredictionMarketLabelQueries(selectedLabel, selectedSubLabel);
  const [search, setSearch] = useState("");

  const [subLabels, setSubLabels] = useState<SubLabelData[]>([]);

  const { predictionMarkets, perPage, isValidating, isLoading, triggerRef, triggerAtPercentage } = useInfiniteScroll(
    useGetPublicPredictionMarkets,
    {
      label: queryLabel,
      subLabel: querySubLabel,
      orderBy,
      search,
      type: getQueryFilterSpecification(selectedSortPredictionMarketType),
      swrOptions: {
        dedupingInterval: 60000, // 60 seconds cache
      },
    },
  );

  const { activeLabels, isLoading: isLoadingActiveLabels } = useGetActiveLabels();

  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.getElementById("main-header");
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);

    // Check for height changes periodically (for banner animation)
    const interval = setInterval(updateHeaderHeight, 100);

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(activeLabels)) {
      // Add "すべて" to the beginning of each subLabel array in activeLabels
      // Collect all unique sub-labels from all activeLabels
      let allSubLabels: string[] = [];
      if (activeLabels && typeof activeLabels === "object") {
        allSubLabels = [
          ...new Set(
            Object.values(activeLabels)
              .flat()
              .filter((l) => l !== "すべて"),
          ),
        ];
      }

      const processedActiveLabels: Record<string, string[]> = {
        人気: ["すべて", ...allSubLabels],
        New: ["すべて", ...allSubLabels],
        ...(activeLabels
          ? Object.fromEntries(
              Object.entries(activeLabels).map(([key, subLabels]) => [
                key,
                ["すべて", ...subLabels.filter((l) => l !== "すべて")],
              ]),
            )
          : {}),
      };
      const processedSubLabels = processedActiveLabels[selectedLabel].map((label: string) => ({
        id: `${selectedLabel}-${label}`,
        label,
      }));
      setSubLabels(processedSubLabels);
      const foundSubLabel =
        processedSubLabels.find((subLabel) => subLabel.id === selectedSubLabel?.id) || processedSubLabels[0];

      // Only update if the sub-label actually changes
      if (selectedSubLabel.id !== foundSubLabel.id) {
        setSelectedSubLabel(foundSubLabel);
      }
    }
  }, [activeLabels, selectedLabel, selectedSubLabel, setSelectedSubLabel]);

  const labels: LabelData[] = [...DEFAULT_LABELS_PREDICTION_MARKET, ...(activeLabels ? Object.keys(activeLabels) : [])];

  const navigateToCommentsAndRanking = (predictionMarketId: string) => {
    navigate(`/prediction-market/details/${predictionMarketId}#comments-and-ranking`);
  };

  const previewPM = isDemo ? generateDemoMarkets(6) : predictionMarkets;

  return (
    <div className="w-full bg-[#FAFAFA] relative">
      <div
        className="sticky z-40 bg-[#FAFAFA] backdrop-blur-xl absolute top-0 px-5 w-100vw relative"
        style={{ top: `${headerHeight}px` }}
      >
        <LabelSelector
          labels={labels}
          selectedLabel={selectedLabel}
          onLabelSelect={(label: LabelData) => {
            setSelectedLabel(label);
          }}
        />
        {isDemo && (
          <SubLabelSelector
            className="mt-[10px]"
            subLabels={[
              { id: "demo-1", label: "すべて" },
              { id: "demo-2", label: "demoサブラベル" },
            ]}
            selectedSubLabel={selectedSubLabel}
            onSubLabelSelect={setSelectedSubLabel}
          />
        )}

        {subLabels && subLabels.length > 0 && (
          <SubLabelSelector
            className="mt-[10px]"
            subLabels={subLabels}
            selectedSubLabel={selectedSubLabel}
            onSubLabelSelect={setSelectedSubLabel}
          />
        )}
        <div className="mt-[10px] pb-[10px] flex flex-row items-center justify-between gap-[9px]">
          <SearchInput setSearch={setSearch} className="flex-1" />
          <SelectSortPredictionMarket
            className="h-[42px]"
            selectedSortPredictionMarketType={selectedSortPredictionMarketType}
            setSelectedSortPredictionMarketType={setSelectedSortPredictionMarketType}
          />
        </div>
        {/* Blur gradient at bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[10px] pointer-events-none translate-y-full"
          style={{
            background: "linear-gradient(to bottom, rgba(250, 250, 250, 1) 0%, rgba(250, 250, 250, 0) 100%)",
          }}
        />
      </div>

      <div className="p-5 grid gap-5 w-full grid-cols-1 sm:grid-cols-2">
        {(isLoadingActiveLabels || isLoading) &&
          Array.from({ length: 6 }).map((_, index) => <LoadingSkeleton key={index} />)}

        {previewPM.map((predictionMarket, index) => {
          const choices = normalizeChoicesPercentage(predictionMarket.predictionMarketChoices);
          const predictionMarketType = getPredictionMarketType(
            choices.map((c: { name: string }) => ({ name: c.name })),
          );
          const fetchPoint = predictionMarkets.length - Math.floor(perPage * triggerAtPercentage);
          const isReachedFetchPoint = index + 1 === fetchPoint;

          return (
            <div key={predictionMarket.id}>
              <MarketCardListItem
                market={predictionMarket}
                choiceComponent={
                  <MultipleChoices
                    isScrollable={false}
                    choices={choices}
                    onButtonClick={(choiceId) => {
                      navigate(`/prediction-market/details/${predictionMarket.id}#${choiceId}`);
                    }}
                    outlined={predictionMarketType === PredictionMarketType.TwoChoice}
                    isShowProbability={true}
                    onCommentClick={() => navigateToCommentsAndRanking(predictionMarket.id)}
                  />
                }
              />
              {!isValidating && isReachedFetchPoint && <div ref={triggerRef} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LoadingSkeleton = () => {
  return (
    <Skeleton className="rounded-xl overflow-hidden">
      <Skeleton className="h-[103px] w-full rounded-none" />

      <div className="px-[15px] pt-2.5 pb-5 flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-14" />
          <Skeleton className="h-6 w-6" />
        </div>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-16 w-full" />
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-24" />
        </div>
      </div>
    </Skeleton>
  );
};
